"use client"

import * as React from "react"
import { useTranslation } from "react-i18next"
import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  Row,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { DataTableViewOptions } from "./data-table-view-options"
import { ArrowUpDown } from "lucide-react"
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { cn } from "@/lib/utils"

// Side-effect import: applies ColumnMeta augmentation globally
import "@/components/ui/column-meta"

interface ManualPaginationConfig {
  pageIndex: number // 1-based index from consumer
  pageSize: number
  totalItems: number
  onPageChange: (page: number) => void
  isLoading?: boolean
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  enableSearch?: boolean
  manualPagination?: ManualPaginationConfig
  onRowClick?: (row: TData) => void
  rowClassName?: (row: TData) => string
  /**
   * Optional custom mobile card renderer. When provided, this is used instead
   * of the automatic column-metadata card layout on screens narrower than md.
   *
   * Receives the full row object and should return the JSX for one card item.
   * The surrounding divide-y list container is provided by DataTable.
   */
  renderMobileCard?: (row: TData) => React.ReactNode
  /**
   * Optional toolbar renderer. When provided, DataTable's built-in toolbar row
   * (search input / view-options button) is suppressed entirely. The function
   * receives the `DataTableViewOptions` node on desktop, or `null` on mobile,
   * so the caller can conditionally render it (e.g. hide the column picker on
   * mobile while still showing tab-switchers or other controls).
   *
   * Example:
   *   toolbarSlot={(viewOptions) => (
   *     <div className="flex items-center justify-between py-4">
   *       <MyTabSwitcher />
   *       {viewOptions}
   *     </div>
   *   )}
   */
  toolbarSlot?: (viewOptions: React.ReactNode) => React.ReactNode
}

export function DataTable<TData, TValue>({
  columns,
  data,
  enableSearch = true,
  manualPagination,
  onRowClick,
  rowClassName,
  renderMobileCard,
  toolbarSlot,
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation('common')
  const isMd = useBreakpoint("md")
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  // User-controlled column visibility (from the view-options dropdown)
  const [userVisibility, setUserVisibility] = React.useState<VisibilityState>({})
  const [localPagination, setLocalPagination] = React.useState({ pageIndex: 0, pageSize: manualPagination?.pageSize ?? 10 })

  // Merge user visibility with breakpoint-driven hideOnMobile overrides.
  // On mobile we hide columns marked hideOnMobile; on desktop we restore them.
  const columnVisibility = React.useMemo<VisibilityState>(() => {
    if (isMd) return userVisibility
    const mobileOverrides: VisibilityState = {}
    columns.forEach(col => {
      if (col.meta?.hideOnMobile) {
        const id = (col as { id?: string; accessorKey?: string }).id
          ?? (col as { accessorKey?: string }).accessorKey as string | undefined
        if (id) mobileOverrides[id] = false
      }
    })
    return { ...userVisibility, ...mobileOverrides }
  }, [isMd, userVisibility, columns])

  const controlledPagination = manualPagination
    ? { pageIndex: Math.max(manualPagination.pageIndex - 1, 0), pageSize: manualPagination.pageSize }
    : localPagination

  const handlePaginationChange = manualPagination
    ? (updater: any) => {
        const next = typeof updater === 'function' ? updater(controlledPagination) : updater
        const newPageIndex = Math.max(0, next.pageIndex)
        if (newPageIndex !== controlledPagination.pageIndex) {
          manualPagination.onPageChange(newPageIndex + 1)
        }
      }
    : setLocalPagination

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setUserVisibility,
    manualPagination: !!manualPagination,
    pageCount: manualPagination ? Math.max(Math.ceil(manualPagination.totalItems / manualPagination.pageSize), 1) : undefined,
    onPaginationChange: handlePaginationChange,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      pagination: controlledPagination,
    },
  })

  const displayedRows = table.getRowModel().rows.length
  const totalRows = manualPagination ? manualPagination.totalItems : table.getFilteredRowModel().rows.length
  const fromRow = manualPagination
    ? Math.min(totalRows, controlledPagination.pageIndex * controlledPagination.pageSize + 1)
    : totalRows === 0 ? 0 : 1
  const toRow = manualPagination
    ? Math.min(totalRows, (controlledPagination.pageIndex + 1) * controlledPagination.pageSize)
    : displayedRows

  const searchColumn = React.useMemo(() => {
    const countryColumn = table.getColumn('country')
    if (countryColumn) {
      return countryColumn
    }
    const leafColumns = table.getAllLeafColumns()
    return leafColumns.find(col => col.getCanFilter && col.getCanFilter()) || leafColumns[0]
  }, [table])

  const showMobileLayout = !isMd

  return (
    <div className="w-full">
      {toolbarSlot
        ? toolbarSlot(showMobileLayout ? null : <DataTableViewOptions table={table} />)
        : (
          <div className="flex items-center justify-between py-4">
            {enableSearch ? (
              <Input
                placeholder={t('table.filterPlaceholder')}
                value={(searchColumn?.getFilterValue?.() as string) ?? ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  searchColumn?.setFilterValue?.(event.target.value)
                }
                className="max-w-sm"
              />
            ) : <span />}
            {/* Hide column-visibility toggle on mobile — not relevant for card layout */}
            {!showMobileLayout && <DataTableViewOptions table={table} />}
          </div>
        )
      }

      {showMobileLayout ? (
        /* ── Mobile: card / list layout ─────────────────────────────────── */
        <div className="max-sm:-mx-6 max-sm:border-x-0 max-sm:rounded-none rounded-md border">
          {table.getRowModel().rows?.length ? (
            <div className="divide-y divide-border">
              {table.getRowModel().rows.map((row) => (
                <div
                  key={row.id}
                  className={cn(
                    onRowClick && "cursor-pointer",
                    rowClassName?.(row.original as TData)
                  )}
                  onClick={() => onRowClick?.(row.original as TData)}
                >
                  {renderMobileCard
                    ? renderMobileCard(row.original as TData)
                    : <AutoMobileCard row={row} />
                  }
                </div>
              ))}
            </div>
          ) : (
            <div className="h-24 flex items-center justify-center text-sm text-muted-foreground">
              {t('table.noResults')}
            </div>
          )}
        </div>
      ) : (
        /* ── Desktop: full table layout ─────────────────────────────────── */
        <div className="max-sm:-mx-6 max-sm:border-x-0 max-sm:rounded-none rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={rowClassName?.(row.original as TData) ?? ''}
                    onClick={() => onRowClick?.(row.original as TData)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    {t('table.noResults')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          {manualPagination
            ? t('table.rowsShown', {
                current: `${fromRow}-${toRow}`,
                total: totalRows
              })
            : t('table.rowsShown', {
                current: displayedRows,
                total: totalRows
              })}
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage() || manualPagination?.isLoading}
          >
            {t('table.previous')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage() || manualPagination?.isLoading}
          >
            {t('table.next')}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Auto mobile card ────────────────────────────────────────────────────────

/**
 * Renders a billing-style two-line flex-row card from column metadata.
 *
 * Layout:
 *   ┌───────────────────────────────────────────┐
 *   │  [icon]  primary text           value     │
 *   │          secondary · metadata    badge    │
 *   └───────────────────────────────────────────┘
 *
 * Columns with meta.cardRole assigned fill each slot.
 * Columns without a cardRole are not shown in the card view.
 */
function AutoMobileCard<TData, TValue>({ row }: { row: Row<TData> }) {
  const cells = row.getVisibleCells()

  const iconCell    = cells.find(c => c.column.columnDef.meta?.cardRole === "icon")
  const primaryCell = cells.find(c => c.column.columnDef.meta?.cardRole === "primary")
  const secondaryCell = cells.find(c => c.column.columnDef.meta?.cardRole === "secondary")
  const valueCell   = cells.find(c => c.column.columnDef.meta?.cardRole === "value")
  const badgeCell   = cells.find(c => c.column.columnDef.meta?.cardRole === "badge")

  // If no cardRole metadata is present on any column, fall back to a simple
  // stacked list of all cell values so the table is at least readable.
  const hasAnyRole = iconCell || primaryCell || secondaryCell || valueCell || badgeCell
  if (!hasAnyRole) {
    return (
      <div className="px-4 py-3 space-y-1">
        {cells.map(cell => (
          <div key={cell.id} className="text-sm">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3 px-4 py-3.5">
      {/* Left: icon/avatar */}
      {iconCell && (
        <div className="shrink-0">
          {flexRender(iconCell.column.columnDef.cell, iconCell.getContext())}
        </div>
      )}

      {/* Centre: primary + secondary */}
      <div className="min-w-0 flex-1">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3">
          {primaryCell ? (
            <div className="min-w-0 text-sm font-medium truncate">
              {flexRender(primaryCell.column.columnDef.cell, primaryCell.getContext())}
            </div>
          ) : <span />}
          {valueCell && (
            <div className="shrink-0 text-sm font-semibold">
              {flexRender(valueCell.column.columnDef.cell, valueCell.getContext())}
            </div>
          )}
        </div>
        {/* Bottom row */}
        {(secondaryCell || badgeCell) && (
          <div className="mt-0.5 flex items-center justify-between gap-3">
            {secondaryCell ? (
              <div className="min-w-0 text-xs text-muted-foreground truncate">
                {flexRender(secondaryCell.column.columnDef.cell, secondaryCell.getContext())}
              </div>
            ) : <span />}
            {badgeCell && (
              <div className="shrink-0">
                {flexRender(badgeCell.column.columnDef.cell, badgeCell.getContext())}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── SortableHeader ──────────────────────────────────────────────────────────

/**
 * Drop-in sortable column header. Pass to `header` in a ColumnDef:
 *
 *   header: ({ column }) => <SortableHeader column={column} label="Date" />
 */
export function SortableHeader<TData, TValue>({
  column,
  label,
}: {
  column: Column<TData, TValue>
  label: React.ReactNode
}) {
  return (
    <Button
      variant="ghost"
      onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      className="h-auto p-0 font-medium"
    >
      {label}
      <ArrowUpDown className="ml-2 h-4 w-4" />
    </Button>
  )
}
